function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '../Spinner';
import { StyledButton, StyledIconContainer, StyledLoadingContainer, StyledFlexContainer, StyledHiddenLabel } from './Button.style';
import { useA11yAnnounce } from './../../foundation/Accessibility/Announcer';
import { getCleanProps } from '../../utility/getCleanProps';
var componentId = 'Button';
Button.propTypes = {
  /** label of the button */
  label: PropTypes.string.isRequired,

  /** variant of the button */
  variant: PropTypes.oneOf(['primary', 'secondary', 'apply', 'alternate']).isRequired,

  /** icon of the button that will show beside the label. usage: <Button label="label" icon={AddIcon} /> */
  icon: PropTypes.func,

  /** renders the right color on a dark background */
  isOnDarkBg: PropTypes.bool,

  /** renders the link styling */
  styledAsLink: PropTypes.bool,

  /** renders the link styling without left and right paddings */
  linkPadding: PropTypes.bool,

  /** renders the icon to the right. default is left icon */
  iconToRight: PropTypes.bool,

  /** type of the button */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),

  /** callback when the button is clicked */
  onClick: PropTypes.func,

  /** render the button as disabled */
  disabled: PropTypes.bool,

  /** change the state to loading. currently does not support styledAsLink. */
  isLoading: PropTypes.bool,

  /** screen reader test to announce when it is loading */
  srLoadingText: PropTypes.string,

  /** reference of the native button element */
  buttonRef: PropTypes.shape({
    current: PropTypes.any
  }),

  /** used for additional aria label if button label is not descriptive enough */
  'aria-label': PropTypes.string,

  /** a list of element id (separated by a space) that describes this button */
  'aria-describedby': PropTypes.string
};
Button.defaultProps = {
  variant: 'primary',
  type: 'button',
  styledAsLink: false,
  linkPadding: true,
  isOnDarkBg: false,
  iconToRight: false,
  srLoadingText: 'Processing please wait'
};
Button.componentId = componentId;
export function Button(props) {
  var label = props.label,
      variant = props.variant,
      icon = props.icon,
      isLoading = props.isLoading,
      srLoadingText = props.srLoadingText,
      iconToRight = props.iconToRight,
      buttonRef = props.buttonRef,
      restProps = _objectWithoutProperties(props, ["label", "variant", "icon", "isLoading", "srLoadingText", "iconToRight", "buttonRef"]);

  var Icon = icon;

  var _useA11yAnnounce = useA11yAnnounce(),
      setA11yMessage = _useA11yAnnounce.setA11yMessage;

  useEffect(function () {
    var interval;

    if (isLoading) {
      setA11yMessage(srLoadingText, 'polite'); // re-announce the message after every 30 seconds

      interval = setInterval(function () {
        setA11yMessage(srLoadingText, 'polite');
      }, 30000);
    }

    return function cleanup() {
      if (interval) {
        setA11yMessage('');
        clearInterval(interval);
      }
    };
  }, [isLoading, srLoadingText, setA11yMessage]);

  var Label = function Label() {
    return isLoading ? /*#__PURE__*/React.createElement(StyledHiddenLabel, null, label) : label;
  };

  var Loader = function Loader() {
    return /*#__PURE__*/React.createElement(StyledLoadingContainer, null, /*#__PURE__*/React.createElement(Spinner, {
      isOnDarkBg: true
    }));
  };

  return /*#__PURE__*/React.createElement(StyledButton, _extends({}, getCleanProps(restProps), {
    variant: variant,
    isLoading: isLoading,
    ref: buttonRef,
    "data-component-id": componentId
  }), Icon ? /*#__PURE__*/React.createElement(StyledFlexContainer, {
    iconToRight: iconToRight
  }, isLoading && /*#__PURE__*/React.createElement(Loader, null), /*#__PURE__*/React.createElement(StyledIconContainer, {
    isLoading: isLoading,
    iconToRight: iconToRight
  }, /*#__PURE__*/React.createElement(Icon, {
    color: !iconToRight ? 'white' : icon.color
  })), /*#__PURE__*/React.createElement(Label, null)) : /*#__PURE__*/React.createElement(React.Fragment, null, isLoading ? /*#__PURE__*/React.createElement(StyledFlexContainer, null, /*#__PURE__*/React.createElement(Loader, null), /*#__PURE__*/React.createElement(Label, null)) : /*#__PURE__*/React.createElement(Label, null)));
}